class CartUpsellStrip extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  async init() {
    try {
      // Fetch cart to determine seed product and current product IDs.
      const cart = await fetch('/cart.js').then((r) => r.json());
      if (!cart.items || cart.items.length === 0) return;

      // Seed from the highest-value line item.
      const sorted = [...cart.items].sort((a, b) => b.final_line_price - a.final_line_price);
      const seedId = sorted[0].product_id;
      const cartProductIds = new Set(cart.items.map((i) => i.product_id));

      const intent = this.dataset.intent || 'related';
      const limit = parseInt(this.dataset.limit, 10) || 8;

      const data = await fetch(
        `/recommendations/products.json?product_id=${seedId}&limit=${limit}&intent=${intent}`
      ).then((r) => r.json());

      const recs = data.products
        .filter((p) => !cartProductIds.has(p.id))
        .slice(0, Math.min(limit, 6));

      if (recs.length === 0) return;

      this.render(recs);
      this.bindEvents();
    } catch (_e) {
      // Fail silently — upsell is non-critical.
    }
  }

  render(products) {
    const heading = this.dataset.heading || 'You might also like';
    this.innerHTML = `
      <div class="cart-upsell-strip">
        <p class="cart-upsell-strip__heading">${this.escHtml(heading)}</p>
        <ul class="cart-upsell-strip__list" role="list">
          ${products.map((p) => this.renderCard(p)).join('')}
        </ul>
      </div>
    `;
  }

  renderCard(product) {
    const variant = product.variants[0];
    const price = this.formatMoney(product.price_min);
    const hasOptions = product.variants.length > 1;
    const available = variant.available;

    const imageHtml = product.featured_image
      ? `<img
          src="${this.resizeImage(product.featured_image, '280x280')}"
          alt="${this.escHtml(product.title)}"
          width="140"
          height="140"
          loading="lazy"
        >`
      : '';

    let actionHtml;
    if (hasOptions) {
      actionHtml = `<a href="${product.url}" class="cart-upsell-card__btn">View Options</a>`;
    } else if (!available) {
      actionHtml = `<span class="cart-upsell-card__sold-out">Sold out</span>`;
    } else {
      actionHtml = `<button
        class="cart-upsell-card__btn"
        type="button"
        data-variant-id="${variant.id}"
        aria-label="Add ${this.escHtml(product.title)} to cart"
      >
        <span class="btn-text">Add to cart</span>
        <svg class="btn-spinner spinner" aria-hidden="true" focusable="false" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </button>`;
    }

    return `
      <li class="cart-upsell-card">
        <a href="${product.url}" class="cart-upsell-card__image-link" tabindex="-1" aria-hidden="true">
          ${imageHtml}
        </a>
        <a href="${product.url}" class="cart-upsell-card__title h4">${this.escHtml(product.title)}</a>
        <span class="cart-upsell-card__price">${price}</span>
        ${actionHtml}
      </li>
    `;
  }

  bindEvents() {
    this.querySelector('.cart-upsell-strip__list').addEventListener('click', (e) => {
      const btn = e.target.closest('[data-variant-id]');
      if (btn) {
        e.preventDefault();
        this.addToCart(btn.dataset.variantId, btn);
      }
    });
  }

  getSectionsToRequest() {
    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl && typeof cartItemsEl.getSectionsToRender === 'function') {
      return cartItemsEl.getSectionsToRender();
    }

    const sections = [];
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems?.dataset.id) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      });
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter?.dataset.id) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }

  applySectionsToDOM(sectionsHtml, itemCount) {
    if (!sectionsHtml) return;

    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl && typeof cartItemsEl.renderSections === 'function') {
      cartItemsEl.renderSections(sectionsHtml, itemCount);
      return;
    }

    this.getSectionsToRequest().forEach((section) => {
      const sectionHtml = sectionsHtml[section.section];
      if (!sectionHtml) return;

      const container = document.getElementById(section.id);
      if (!container) return;

      const target = container.querySelector(section.selector) || container;
      const fresh = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .querySelector(section.selector);
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
  }

  async addToCart(variantId, button) {
    button.disabled = true;
    button.classList.add('loading');

    try {
      const sectionsToRequest = this.getSectionsToRequest();
      const body = {
        id: Number(variantId),
        quantity: 1,
        properties: { '_Source': 'Cart Page Upsell' },
      };

      if (sectionsToRequest.length) {
        body.sections = sectionsToRequest.map((section) => section.section);
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || 'Could not add to cart');
      }

      this.applySectionsToDOM(data.sections, data.item_count);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          cart: data,
          sections: data.sections,
          item_count: data.item_count,
          source: 'cart-upsell',
        });
      }

      button.classList.remove('loading');
      button.querySelector('.btn-text').textContent = 'Added!';
      setTimeout(() => {
        button.querySelector('.btn-text').textContent = 'Add to cart';
        button.disabled = false;
      }, 1500);
    } catch (_e) {
      button.disabled = false;
      button.classList.remove('loading');
    }
  }

  formatMoney(cents) {
    const format = window.cartUpsellMoneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      const value = (number / 100).toFixed(precision);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const centsPart = precision > 0 && parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;

    let value;
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }

    return format.replace(match[0], value);
  }

  resizeImage(url, size) {
    if (!url) return '';
    return url.replace(/(\.[^.?]+)(\?.*)?$/, `_${size}$1$2`);
  }

  escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('cart-upsell-strip', CartUpsellStrip);
